import { TARGET_SCOPES } from './targetDialogModel';

const SCOPE_TO_WIRE = Object.freeze({
  scope1: 'SCOPE_1',
  scope2: 'SCOPE_2',
  scope3: 'SCOPE_3',
});

const WIRE_TO_SCOPE = Object.freeze({
  SCOPE_1: 'scope1',
  SCOPE_2: 'scope2',
  SCOPE_3: 'scope3',
});

const yearsBetween = (baselineYear, targetYear) => Array.from(
  { length: Number(targetYear) - Number(baselineYear) + 1 },
  (_, index) => Number(baselineYear) + index,
);

/** Map one backend target period into the UI target-cycle shape. */
export function normalizeTargetPeriod(period) {
  if (!period) return null;
  const baselineYear = Number(period.baseline_fiscal_year);
  const targetYear = Number(period.target_fiscal_year);
  const targets = Object.fromEntries((period.scope_pathways || []).map((pathway) => {
    const scope = WIRE_TO_SCOPE[pathway.scope];
    return [scope, {
      scope,
      baselineYear,
      targetYear,
      reductionPercent: Number(pathway.final_reduction_percent),
      pathwayMode: pathway.pathway_method === 'CUSTOM' ? 'custom' : 'automatic',
      annualReductionPercentages: Object.fromEntries(
        (pathway.annual_reductions || []).map(point => [
          Number(point.fiscal_year),
          point.reduction_percent,
        ]),
      ),
      milestones: [],
    }];
  }));
  return {
    id: period.id,
    name: period.name,
    baseYear: baselineYear,
    targetYear,
    status: period.status,
    targets,
    baseline: period.baseline,
    finalTargetTco2e: period.final_target_tco2e,
    annualAbsoluteTargets: period.annual_absolute_targets,
    isHistoricalTarget: Boolean(period.is_historical_target),
  };
}

/** Map every backend period into the UI cycle list, dropping malformed rows. */
export function targetPeriodsToCycles(periods = []) {
  return periods.map(normalizeTargetPeriod).filter(Boolean);
}

/** Serialize one dialog target batch into the backend upsert contract. */
export function serializeTargetPeriodDraft(targetBatch) {
  const first = targetBatch?.scope1;
  const baselineYear = Number(first?.baselineYear);
  const targetYear = Number(first?.targetYear);
  const scope_pathways = TARGET_SCOPES.map((scope) => {
    const draft = targetBatch[scope];
    const isCustom = draft?.pathwayMode === 'custom' || draft?.trajectoryType === 'custom';
    if (isCustom) {
      const annualReductions = draft?.annualReductionPercentages || {};
      return {
        scope: SCOPE_TO_WIRE[scope],
        pathway_method: 'CUSTOM',
        annual_reductions: yearsBetween(baselineYear, targetYear)
          .filter(year => year > baselineYear)
          .map(year => ({
            fiscal_year: year,
            reduction_percent: String(Number(annualReductions[year])),
          })),
      };
    }
    return {
      scope: SCOPE_TO_WIRE[scope],
      pathway_method: 'AUTOMATIC',
      final_reduction_percent: String(Number(draft?.reductionPercent)),
    };
  });
  return {
    name: String(targetBatch?.periodName || '').trim(),
    baseline_fiscal_year: baselineYear,
    target_fiscal_year: targetYear,
    scope_pathways,
  };
}

/** Adapt yearly-overview rows into the dialog inventory shape. */
export function baselineYearsToInventories(overviewYears = []) {
  return overviewYears.map(row => ({
    year: Number(row.fiscal_year),
    scope1: Number(row.scope_1),
    scope2: Number(row.scope_2),
    scope3: Number(row.scope_3),
  }));
}